import {
  LISTINGS_API,
  MLSListing,
  MLSOUID,
  ToolkitListingID,
  isMLSOUID,
  isToolkitListingID,
  isUndefined,
  mlsOUIDByMLSID,
  parseToolkitListingID,
} from '@aloft/toolkit-shared';
import { logger } from '@sentry/react';

import { fetchGetListings } from '../../../hooks/api/toolkitComponents.ts';
import { ToolkitFetcherConfig } from '../../../hooks/api/toolkitContext.ts';
import { Batcher } from '../../../utils/batcher/index.ts';
import { ListingResult } from './types.ts';

export const getMlsListingsBatcher = (): Batcher.Batcher<
  ListingResult,
  ToolkitListingID,
  ToolkitFetcherConfig
> =>
  Batcher.create<ListingResult, ToolkitListingID, ToolkitFetcherConfig>({
    fetcher: async (ids: ToolkitListingID[], fetcherOptions?: ToolkitFetcherConfig) => {
      if (!fetcherOptions) throw Error('Must provide fetcher options');

      const batches: { [key in MLSOUID]?: string[] } = ids.reduce(
        (acc: { [key in MLSOUID]?: string[] }, id: ToolkitListingID) => {
          const { mls, listingID } = parseToolkitListingID(id);
          const mlsOUID = isMLSOUID(mls) ? mls : mlsOUIDByMLSID(mls);

          if (isUndefined(mlsOUID)) {
            logger.warn(
              `getMlsListingsBatcher: Invalid MLSOUID for listing ID ${listingID} with MLS ${mls}`,
            );
            return acc; // Skip if mlsOUID is undefined
          }

          const existingListingIDs = acc[mlsOUID] ?? [];
          if (!existingListingIDs.includes(listingID)) {
            acc[mlsOUID] = existingListingIDs.concat(listingID);
          }

          return acc;
        },
        {},
      );
      const responses = Object.entries(batches).map(async ([mlsOUID, listingIDs]) => {
        const response = await fetchGetListings({
          ...fetcherOptions,
          body: LISTINGS_API.POST.REQUEST.BODY.parse({
            mlsOUID,
            listingIDs,
          }),
        });

        return (response.results as MLSListing[]) ?? [];
      });

      return Promise.all(responses).then((results) =>
        results.flat().map((listing) => ({ status: 'found', listing })),
      );
    },
    resolver: (items, toolkitListingID) => {
      if (!isToolkitListingID(toolkitListingID)) return { status: 'missing' };

      const { listingID } = parseToolkitListingID(toolkitListingID);
      return (
        items.find(
          (item) =>
            item.status === 'found' &&
            item.listing.ToolkitListingID &&
            parseToolkitListingID(item.listing.ToolkitListingID).listingID === listingID,
        ) ?? { status: 'missing' }
      );
    },
    scheduler: Batcher.windowScheduler(10), // 10 ms
  });
